import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { Bookmark } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { Modal } from '../../../Shared/Modal/Modal';
import { Pagination } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { ColorBadge } from '../../../Shared/UI/ColorBadge';
import { Icon } from '../../../Shared/UI/Icon';
import { Pager } from '../../../Shared/UI/Pager';
import { SearchFilterInput } from '../../../Shared/UI/SearchFilterInput';
import { useRouter } from '../../router';
import { Charity, DonationForm, SharedProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { SettingsLayout } from '../SettingsLayout';
import AddDonationFormForm from './AddDonationFormForm';

export interface DonationFormsPageProps extends SharedProps {
  donationForms: Pagination<DonationForm & {
    subscriptions_count: number;
    oneoff_donations_count: number;
    revenue: number;
  }>;
}

export default function DonationFormsPage({
  donationForms,
  charity,
  params,
}: DonationFormsPageProps) {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(!!params.add_donation_form);

  const containerSize = params.search || donationForms.meta.total > 0 ? 'lg' : 'md';
  const showSearch = params.search || donationForms.meta.total > 0;

  return (
    <>
      <Header>
        <Container size={containerSize}>
          <div className="flex items-center justify-between">
            <HeaderTitle>
              {t('backend:donation_forms')}
            </HeaderTitle>
            <Button onClick={() => setShowModal(true)}>
              {t('backend:add_donation_form')}
            </Button>
          </div>
        </Container>
      </Header>

      <Container size={containerSize} className="space-y-6">
        {showSearch && (
          <div className="w-96">
            <SearchFilterInput id="search" />
          </div>
        )}

        {donationForms.data.length > 0 && (
          <div className="space-y-2">
            {donationForms.data.map((donationForm) => (
              <DonationFormCard
                donationForm={donationForm}
                charity={charity}
                key={donationForm.id}
              />
            ))}
          </div>
        )}

        {donationForms.meta.last_page > 1 && (
          <Pager data={donationForms} />
        )}

        {!!params.search && donationForms.meta.total === 0 && (
          <Card className="py-24 text-center text-slate-500">
            <img
              src={assetUrl('images/no-results.svg')}
              alt={t('backend:no_results')}
              width={120}
              className="block mx-auto mb-6"
            />
            {t('backend:no_results')}
          </Card>
        )}
      </Container>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="sm">
        <AddDonationFormForm />
      </Modal>
    </>
  );
}

DonationFormsPage.layout = (page: ReactNode) => <Layout><SettingsLayout>{page}</SettingsLayout></Layout>;

function DonationFormCard({
  donationForm,
  charity,
}: {
  donationForm: DonationForm & {
    subscriptions_count: number;
    oneoff_donations_count: number;
    revenue: number;
  };
  charity: Charity;
}) {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const { routes } = useRouter();

  const muted = !donationForm.enabled && 'opacity-50';

  return (
    <Card
      spacing="xs"
      link={{
        href: routes.donation_form_page(donationForm.id),
      }}
    >
      <div className="grid grid-cols-[32px_5fr_3fr_3fr_3fr_100px] gap-4 items-center">
        <div className={classNames(muted)}>
          <ColorBadge name={donationForm.internal_title} color={charity.brand_color || null} className="!rounded" />
        </div>
        <div className="min-w-0">
          <h4 className={classNames(muted, 'whitespace-nowrap overflow-hidden text-ellipsis')}>
            {donationForm.internal_title}
          </h4>
        </div>
        <div className={classNames(muted, 'text-sm')}>
          <span className="font-medium">
            {donationForm.oneoff_donations_count}
          </span>
          {' '}
          {t('backend:oneoff_donations', { count: donationForm.oneoff_donations_count }).toLowerCase()}
        </div>
        <div className={classNames(muted, 'text-sm')}>
          <span className="font-medium">
            {donationForm.subscriptions_count}
          </span>
          {' '}
          {t('backend:recurring_donations', { count: donationForm.subscriptions_count }).toLowerCase()}
        </div>
        <div className={classNames(muted, 'text-sm')}>
          <span className="font-medium">
            {formatCurrency(donationForm.revenue)}
          </span>
          {' '}
          {t('backend:raised').toLowerCase()}
        </div>
        {!donationForm.default && (
          <div className={classNames('text-right text-sm font-medium', donationForm.enabled ? 'text-emerald-600' : 'text-slate-500')}>
            {donationForm.enabled ? t('backend:live') : t('backend:offline')}
          </div>
        )}
        {donationForm.default && (
          <div className="text-sm font-medium text-right text-blue-500">
            <Icon className="mr-1">
              <Bookmark />
            </Icon>
            {t('backend:default')}
          </div>
        )}
      </div>
    </Card>
  );
}
